import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { gsap } from 'gsap';
import { SplitSectionSlide } from '@/types';
import { getUploadUrl } from '@/config/api';
import { ANIMATION_CONFIG } from '@/config/animations';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';
import TextOverlay from './TextOverlay';
import { useCursorContext } from '@/providers/CursorProvider';
import RotateDeviceIndicator from '@/components/RotateDeviceIndicator';

interface SplitSectionProps {
    slide: SplitSectionSlide;
    isActive?: boolean;
}

const SplitSection: React.FC<SplitSectionProps> = ({ slide, isActive }) => {
    const leftRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isInNavigationZone, setIsInNavigationZone] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { setCursorType } = useCursorContext();

    const handleImageClick = (e: React.MouseEvent) => {
        if (slide.image_url && !isDragging) {
            if (isInNavigationZone) return;
            window.open(slide.image_url, '_blank', 'noopener,noreferrer');
        }
    };
    const hasAnimated = useRef(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isActive && !hasAnimated.current) {
            hasAnimated.current = true;
            const tl = gsap.timeline();

            if (leftRef.current) {
                tl.fromTo(
                    leftRef.current,
                    { opacity: 0, x: -100 },
                    {
                        opacity: 1,
                        x: 0,
                        duration: ANIMATION_CONFIG.components.splitSection.duration,
                        ease: ANIMATION_CONFIG.components.splitSection.ease
                    },
                    0
                );
            }

            if (rightRef.current) {
                tl.fromTo(
                    rightRef.current,
                    { opacity: 0, x: 100 },
                    {
                        opacity: 1,
                        x: 0,
                        duration: ANIMATION_CONFIG.components.splitSection.duration,
                        ease: ANIMATION_CONFIG.components.splitSection.ease
                    },
                    0
                );
            }
        } else if (!isActive) {
            hasAnimated.current = false;
            if (leftRef.current) {
                gsap.set(leftRef.current, { opacity: 0, x: -100 });
            }
            if (rightRef.current) {
                gsap.set(rightRef.current, { opacity: 0, x: 100 });
            }
        }
    }, [isActive]);

    const renderMedia = (mediaFile?: any, videoUrl?: string, isExternal?: boolean, platform?: string) => {
        if (isExternal && videoUrl) {
            const embedUrl = getEmbedUrl(videoUrl, platform);
            return (
                <iframe
                    src={embedUrl}
                    title="media"
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        objectFit: 'cover',
                        pointerEvents: 'none'
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            );
        } else if (mediaFile?.url) {
            const isVideo = mediaFile.mime?.startsWith('video/') || mediaFile.url.match(/\.(mp4|webm|ogg)$/i);

            if (isVideo) {
                return (
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none'
                        }}
                    >
                        <source src={getUploadUrl(mediaFile.url)} type="video/mp4" />
                    </video>
                );
            } else {
                // Use fit_variant to determine objectFit for both mobile and desktop
                const fitVariant = slide.fit_variant || 'fill';
                let imageStyle: React.CSSProperties = {};

                if (fitVariant === 'horizontal') {
                    // Fit horizontally (left to right)
                    imageStyle = { width: '100%', height: 'auto', objectFit: 'contain' };
                } else if (fitVariant === 'vertical') {
                    // Fit vertically (top to bottom)
                    imageStyle = { width: 'auto', height: '100%', objectFit: 'contain' };
                } else if (fitVariant === 'contained') {
                    // Contained - image will NOT be cut from any sides
                    imageStyle = { width: '100%', height: '100%', objectFit: 'contain' };
                } else {
                    // Fill full window
                    imageStyle = { width: '100%', height: '100%', objectFit: 'cover' };
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        {slide.image_url ? (
                            <div onClick={handleImageClick} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'default' }}>
                                <ImageWithSkeleton
                                    src={getUploadUrl(mediaFile.url)}
                                    alt="media"
                                    loading={isActive ? "eager" : "lazy"}
                                    style={imageStyle}
                                />
                            </div>
                        ) : (
                            <ImageWithSkeleton
                                src={getUploadUrl(mediaFile.url)}
                                alt="media"
                                loading={isActive ? "eager" : "lazy"}
                                style={imageStyle}
                            />
                        )}
                    </div>
                );
            }
        }
        return null;
    };

    const renderLeftSide = () => {
        if (slide.left_side_type === 'text') {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        backgroundColor: slide.left_background_color || 'transparent'
                    }}
                >
                    {(isMobile ? (slide.left_mobile_text_content || '') : slide.left_text_content) && (
                        <TextOverlay
                            text_content={isMobile ? (slide.left_mobile_text_content || '') : (slide.left_text_content || '')}
                            background_color={slide.left_background_color}
                            isActive={isActive}
                        />
                    )}
                </Box>
            );
        } else {
            // Media
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: slide.left_background_color || '#000'
                    }}
                >
                    {renderMedia(slide.left_media, slide.left_video_url, slide.left_is_external_video, slide.left_video_platform)}
                </Box>
            );
        }
    };

    const renderRightSide = () => {
        if (slide.right_side_type === 'text') {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        backgroundColor: slide.right_background_color || 'transparent'
                    }}
                >
                    {(isMobile ? (slide.right_mobile_text_content || '') : slide.right_text_content) && (
                        <TextOverlay
                            text_content={isMobile ? (slide.right_mobile_text_content || '') : (slide.right_text_content || '')}
                            background_color={slide.right_background_color}
                            isActive={isActive}
                        />
                    )}
                </Box>
            );
        } else {
            // Media
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: slide.right_background_color || '#000'
                    }}
                >
                    {renderMedia(slide.right_media, slide.right_video_url, slide.right_is_external_video, slide.right_video_platform)}
                </Box>
            );
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100vh',
                display: 'flex',
                flexDirection: {
                    xs: 'row', // Always row (left/right) on mobile
                    md: 'row'
                },
                '@media (orientation: portrait) and (max-width: 899px)': {
                    flexDirection: 'column' // Only use column in portrait mode on small screens
                },
                position: 'relative'
            }}
        >
            {/* Left Half */}
            <Box
                ref={leftRef}
                sx={{
                    width: {
                        xs: '50%', // 50% width in landscape
                        md: '50%'
                    },
                    height: {
                        xs: '100vh', // Full height in landscape
                        md: '100vh'
                    },
                    overflow: 'hidden',
                    backgroundColor: slide.left_background_color || '#000',
                    '@media (orientation: portrait) and (max-width: 899px)': {
                        width: '100%',
                        height: '50vh'
                    },
                }}
            >
                {renderLeftSide()}
            </Box>

            {/* Right Half */}
            <Box
                ref={rightRef}
                sx={{
                    width: {
                        xs: '50%', // 50% width in landscape
                        md: '50%'
                    },
                    height: {
                        xs: '100vh', // Full height in landscape
                        md: '100vh'
                    },
                    overflow: 'hidden',
                    backgroundColor: slide.right_background_color || '#000',
                    '@media (orientation: portrait) and (max-width: 899px)': {
                        width: '100%',
                        height: '50vh'
                    }
                }}
            >
                {renderRightSide()}
            </Box>

            {/* Always show text overlay if overlay_text exists */}
            {(isMobile ? (slide.overlay_mobile_text || '') : slide.overlay_text) && (
                <TextOverlay
                    text_content={isMobile ? (slide.overlay_mobile_text || '') : (slide.overlay_text || '')}
                    isActive={isActive}
                />
            )}

            {/* Navigation Zones */}
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', left: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', right: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'auto' }}
            />
            
            <RotateDeviceIndicator show={slide.enable_rotate && isActive} />
        </Box>
    );
};

// Helper function to get embed URLs
const getEmbedUrl = (url: string, platform?: string): string => {
    if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = extractYouTubeId(url);
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1`;
    } else if (platform === 'vimeo' || url.includes('vimeo.com')) {
        const videoId = extractVimeoId(url);
        return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
    }
    return url;
};

const extractYouTubeId = (url: string): string => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : '';
};

const extractVimeoId = (url: string): string => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
};

export default SplitSection;
